import { useOpenSearchContext } from 'hooks/useOpenSearchContext';
import { TextFieldCustomProps } from 'open-search/types';
import { KeyboardEvent } from 'react';
import { clearMask, formatCPF, phoneMask } from 'utils';
import { isEmpty, isPhone, validateCpf } from 'utils/validate';
import { TextsSearchTrucker } from '../types';
import { useClearOpenSearch } from '../useClearOpenSearch';

const BACKSPACE_KEY = 'Backspace';
const NUMBER_REGEX = /^\d$/g;
const SEARCH_TERM_LIMIT = 11;

export function useTextFieldSearchTrucker() {
  const {
    isTextFieldFocus,
    valueInputSearch,
    inputHasError,
    setIsInputSearchMasked,
    setInputHasError,
    setIsTextFieldFocus,
    setValueInputSearch,
    setIsCardVisible,
    setIsLastSearchedDriversVisible,
  } = useOpenSearchContext();

  const { invalidateOpenSearchQuery } = useClearOpenSearch();

  const onFocusTextField = () => {
    setIsTextFieldFocus(true);
    /* istanbul ignore else */
    if (valueInputSearch?.length <= 11) {
      setIsLastSearchedDriversVisible(true);
    }

    /* istanbul ignore else */
    if (valueInputSearch?.length === 0) {
      invalidateOpenSearchQuery();
    }
  };

  const hasNonNumeric = (term: string): boolean => !/^\d+$/.test(term) && term !== '';

  const isValidCharacter = (key: string): boolean => !!(key.match(NUMBER_REGEX) || key === BACKSPACE_KEY);

  const removeTrailingWhitespaces = (term: string | undefined): string | undefined => term?.trim();

  const hasError = (term: string) => hasNonNumeric(term) || term.length > SEARCH_TERM_LIMIT;

  const getFormatedValue = (term: string) => {
    const isCpf = validateCpf(term);

    if (isCpf && isPhone(term)) {
      setIsInputSearchMasked(false);
      return term;
    }

    setIsInputSearchMasked(true);

    const formatedValueInput = isCpf ? formatCPF(term) : phoneMask(term);

    return formatedValueInput;
  };

  const getTermFromEvent = (event: KeyboardEvent | ClipboardEvent) => {
    const target = event.target as HTMLInputElement;

    const { value: term } = target;

    return term;
  };

  const handleKeyPress = (event: KeyboardEvent): void => {
    event.stopPropagation();

    const term = getTermFromEvent(event);
    const { key } = event;

    /* istanbul ignore else  */
    if (!isValidCharacter(key) || term?.length >= 11) {
      event.preventDefault();
    }
  };

  const updateValueInput = (event: KeyboardEvent) => {
    event.stopPropagation();

    if (event.key === BACKSPACE_KEY) {
      invalidateOpenSearchQuery();
      setIsLastSearchedDriversVisible(true);
    }

    let term = getTermFromEvent(event);
    term = removeTrailingWhitespaces(term);

    if (isEmpty(term)) {
      setValueInputSearch('');
      setIsCardVisible(false);
      setInputHasError(false);
      return;
    }

    const clearedTerm = clearMask(term);

    const inputedHasError = hasError(clearedTerm);
    setInputHasError(inputedHasError);

    if (inputedHasError) return;

    if (clearedTerm?.length === 11) {
      const formatedValueInput = getFormatedValue(clearedTerm);
      setValueInputSearch(formatedValueInput);
      setIsLastSearchedDriversVisible(false);
      return;
    }

    setIsLastSearchedDriversVisible(true);
    setValueInputSearch(clearedTerm);
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    event.stopPropagation();
    invalidateOpenSearchQuery();

    const pastedTerm = event.clipboardData.getData('text/plain');
    const clearedTerm = clearMask(pastedTerm);

    if (valueInputSearch && clearedTerm !== clearMask(valueInputSearch)) {
      setValueInputSearch(null);
    }

    /* 
      await needed for get time to react see the value change when user
      select the text inside the input and paste another text over it
    */
    await new Promise((r) => {
      setTimeout(r, 10);
    });

    const inputedHasError = hasError(clearedTerm);
    setInputHasError(inputedHasError);

    if (inputedHasError) {
      setValueInputSearch(clearedTerm);
      return;
    }

    if (clearedTerm?.length === 11) {
      const formatedValueInput = getFormatedValue(clearedTerm);
      setValueInputSearch(formatedValueInput);
      return;
    }
    setValueInputSearch(clearedTerm);
  };

  const propsTextField: TextFieldCustomProps = {
    states: {
      placeholder: TextsSearchTrucker.textFieldPlaceholder,
      valueInput: valueInputSearch,
      hasError: inputHasError,
      errorMessage: TextsSearchTrucker.textFieldInvalidErrorMessage,
      isTextFieldFocus,
    },
    events: {
      onFocus: onFocusTextField,
      onKeyUp: updateValueInput,
      onKeyPress: handleKeyPress,
      onPaste: handlePaste,
      setIsTextFieldFocus,
    },
  };

  return { propsTextField };
}
